import { useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { navigate, usePath } from '../../lib/router'
import { Wordmark } from '../brand/Wordmark'
import { buttonVariants, Button } from '../ui/button'
import { ThemeToggle } from '../ui/theme-toggle'

/**
 * Floating liquid-glass nav island (doWallet public header): centered,
 * rounded, translucent, always visible. Brand left, journey links centered,
 * quiet login + volt CTA right.
 */
export function PublicHeader({ loggedIn, nav = [] }: { loggedIn: boolean; nav?: Array<{ href: string; label: string }> }) {
  const path = usePath()
  const [open, setOpen] = useState(false)
  const appHref = loggedIn ? '/dashboard' : '/login'
  const appLabel = loggedIn ? 'Dashboard' : 'Log in'
  const go = (href: string) => (e: React.MouseEvent) => {
    if (href.startsWith('/#') || href.startsWith('#')) return
    e.preventDefault(); navigate(href); setOpen(false)
  }
  return (
    <header className="pointer-events-none fixed inset-x-0 top-3 z-40 flex justify-center px-4 sm:top-4">
      <div className={cn(
        'pointer-events-auto flex h-14 w-full max-w-[53rem] items-center justify-between gap-1 rounded-full border border-border/60 pl-6 pr-2 md:grid md:grid-cols-[1fr_auto_1fr]',
        'bg-background/85 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)] backdrop-blur-lg backdrop-saturate-150 supports-[backdrop-filter]:bg-background/75',
      )}>
        <a href="/" onClick={go('/')} aria-label="Doable. Start page" className="mr-1 justify-self-start transition-opacity hover:opacity-70"><Wordmark /></a>
        <nav className="hidden items-center gap-1 md:flex">
          {nav.map((n) => {
            const active = path === n.href
            return (
              <a key={n.href} href={n.href} onClick={go(n.href)} aria-current={active ? 'page' : undefined}
                className={cn('rounded-full px-3.5 py-1.5 text-sm transition-colors', active ? 'relative bg-foreground/5 font-medium text-foreground after:absolute after:inset-x-3.5 after:bottom-[3px] after:h-[2px] after:rounded-full after:bg-volt' : 'text-muted-foreground hover:text-foreground')}>
                {n.label}
              </a>
            )
          })}
        </nav>
        <div className="hidden items-center gap-1.5 justify-self-end md:flex">
          <ThemeToggle />
          <a href={appHref} onClick={go(appHref)} className={buttonVariants({ variant: 'outline', size: 'sm', className: 'ml-0.5 h-9' })}>{appLabel}</a>
          <a href="/signup" onClick={go('/signup')} className={buttonVariants({ size: 'sm', className: 'h-9 font-semibold' })}>Request access</a>
        </div>
        <div className="flex items-center gap-1 md:hidden">
          <a href={appHref} onClick={go(appHref)} className={buttonVariants({ size: 'sm', className: 'font-semibold' })}>{appLabel}</a>
          <Button variant="ghost" size="icon" className="size-9" aria-label={open ? 'Close menu' : 'Menu'} onClick={() => setOpen((v) => !v)}>{open ? <X /> : <Menu />}</Button>
        </div>
      </div>
      {open ? (
        <div className="pointer-events-auto absolute inset-x-4 top-[4.25rem] rounded-xl bg-popover p-2 text-sm ring-1 ring-foreground/10 shadow-float md:hidden">
          <a href="/signup" onClick={go('/signup')} className="block rounded-lg px-3 py-2 font-medium hover:bg-muted">Request access</a>
          <div className="my-1 border-t" />
          {nav.map((n) => <a key={n.href} href={n.href} onClick={go(n.href)} className="block rounded-lg px-3 py-2 text-muted-foreground hover:bg-muted hover:text-foreground">{n.label}</a>)}
        </div>
      ) : null}
    </header>
  )
}
